import PageHeader from "@/components/page-header";
import {
  TitleAccountForm,
  titleAccountFormValues,
} from "@/components/title/title-account-form";
import { TitleAccountUsers } from "@/components/title/title-account-users";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import ErrorComponent from "@/components/ui/error";
import LoadingScreen from "@/components/ui/loading-screen";
import { toast } from "@/components/ui/sonner";
import {
  generatePasswordResetUrlMutation,
  getTitleAccountOptions,
  updateTitleAccountMutation,
} from "@/hera-api/client/@tanstack/react-query.gen";
import { useAuthQuerySetup } from "@/hooks/use-auth-query-setup";
import { getQueryErrorHeading, getQueryErrorMessage } from "@/lib/utils";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";

export const Route = createFileRoute("/title/account")({
  component: TitleAccountPage,
});

export function TitleAccountPage() {
  const { isAuthSetupComplete } = useAuthQuerySetup();
  const [resetting, setResetting] = useState(false);
  const account = useQuery({
    ...getTitleAccountOptions(),
    enabled: isAuthSetupComplete,
  });
  const update = useMutation({ ...updateTitleAccountMutation() });
  const resetPassword = useMutation({ ...generatePasswordResetUrlMutation() });

  if (!isAuthSetupComplete || account.isLoading) {
    return <LoadingScreen />;
  }
  if (account.error || !account.data) {
    return (
      <ErrorComponent
        title={getQueryErrorHeading(account.error)}
        message={getQueryErrorMessage(account.error)}
      />
    );
  }

  const data = account.data;
  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-6">
      <PageHeader title="Account" hideBackButton />
      <Card>
        <CardHeader>
          <CardTitle>Details</CardTitle>
        </CardHeader>
        <CardContent>
          <TitleAccountForm
            initial={titleAccountFormValues(data)}
            isSecondary={Boolean(data.primary_id)}
            submitLabel="Save"
            busy={update.isPending}
            onSubmit={async (values) => {
              await update.mutateAsync({
                body: {
                  name: values.name,
                  company_name: values.company_name,
                  phone: values.phone,
                  address1: values.address1,
                  address2: values.address2,
                  city: values.city,
                  state: values.state,
                  zipcode: values.zipcode,
                },
              });
              await account.refetch();
              toast({
                title: "Saved",
                description: "Account information updated.",
              });
            }}
          />
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Password</CardTitle>
        </CardHeader>
        <CardContent>
          <Button
            type="button"
            variant="outline"
            disabled={resetting}
            onClick={() => {
              setResetting(true);
              void resetPassword
                .mutateAsync({})
                .then((response) => {
                  window.location.href = response.reset_url;
                })
                .catch(() => setResetting(false));
            }}
          >
            Go to password reset page
          </Button>
        </CardContent>
      </Card>
      {data.is_admin ? (
        <Card>
          <CardContent className="pt-6">
            <TitleAccountUsers
              currentAccountId={data.id}
              isPrimary={data.primary_id == null}
            />
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
